import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ManufacturerRatingBadge } from '@/components/manufacturer-rating-badge'
import {
  MANUFACTURER_RATINGS,
  ratingLabel,
  type ManufacturerRatingKind,
  type ManufacturerRatingValue,
} from '@/lib/manufacturer-ratings'
import { formatStatus } from '@/lib/utils'
import { StatusPill } from '@/components/status-pill'
import { termsPill } from '@/lib/suppliers'
import { LIST_RATING_KINDS, freightProgram } from '@/lib/manufacturer-list'
import type { components } from '@/api/schema'

type Manufacturer = components['schemas']['ManufacturerListRead']
type RatingCount = components['schemas']['ManufacturerRatingCountRead']

function trackingLabels(mfr: Manufacturer) {
  if (mfr.aq_tracking) return ['Quoting', 'Storefront']
  return mfr.quote_tracking ? ['Quoting'] : []
}

function location(mfr: Manufacturer) {
  return [mfr.city, mfr.state].filter(Boolean).join(', ')
}

/** One kind's values, in the kind's own order (best last becomes best first), with category counts. */
function ratingValues(mfr: Manufacturer, kind: ManufacturerRatingKind): RatingCount[] {
  const order = MANUFACTURER_RATINGS[kind].options.map((option) => option.value as string).reverse()
  return (mfr.ratings ?? [])
    .filter((row) => row.rating === kind)
    .sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))
}

function RatingChips({ mfr, kind }: { mfr: Manufacturer; kind: ManufacturerRatingKind }) {
  const values = ratingValues(mfr, kind)
  if (!values.length) return <span className="text-xs text-muted-foreground">-</span>
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((row) => {
        const value = row.value as ManufacturerRatingValue[typeof kind]
        const label = ratingLabel(kind, value)
        const categories = `${row.count} ${row.count === 1 ? 'category' : 'categories'}`
        return (
          <ManufacturerRatingBadge key={row.value} rating={kind} value={value}>
            <span title={`${label} in ${categories}`}>
              {label}
              {row.count > 1 && <span className="tabular-nums opacity-75"> ×{row.count}</span>}
              <span className="sr-only"> in {categories}</span>
            </span>
          </ManufacturerRatingBadge>
        )
      })}
    </div>
  )
}

function StatusBadge({ status, className }: { status: string; className?: string }) {
  return (
    <Badge variant={status === 'preferred' ? 'default' : 'secondary'} className={className}>
      {formatStatus(status)}
    </Badge>
  )
}

/** Payment terms, so accounting can see who is still CIA (USA-1535); none on file shows nothing. */
function TermsPill({ mfr }: { mfr: Manufacturer }) {
  if (!mfr.payment_terms_type || mfr.payment_terms_type === 'unknown') return null
  const pill = termsPill(mfr)
  return <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
}

function TrackingBadges({ mfr }: { mfr: Manufacturer }) {
  return trackingLabels(mfr).map((label) => (
    <Badge key={label} variant="outline" className="text-xs text-muted-foreground">
      {label}
    </Badge>
  ))
}

function shippingFacts(mfr: Manufacturer) {
  return [
    mfr.lead_time_days != null ? `Lead time ${mfr.lead_time_days}d` : null,
    mfr.free_shipping ? 'Free shipping' : null,
  ].filter((fact): fact is string => !!fact)
}

function FreightProgram({ mfr, linkMore }: { mfr: Manufacturer; linkMore: boolean }) {
  const program = freightProgram(mfr)
  if (!program) return null
  return (
    <p>
      {program.text}
      {program.more &&
        (linkMore ? (
          <>
            {' · '}
            <Link
              to="/manufacturers/$manuId"
              params={{ manuId: mfr.uuid }}
              className="text-primary hover:underline"
            >
              More terms
            </Link>
          </>
        ) : (
          ' · more terms'
        ))}
    </p>
  )
}

function competitorFacts(mfr: Manufacturer) {
  return [
    mfr.below_floor_products
      ? `${mfr.below_floor_products.toLocaleString()} below our floor`
      : null,
    mfr.below_map_products ? `${mfr.below_map_products.toLocaleString()} below MAP` : null,
  ].filter((fact): fact is string => !!fact)
}

export function ManufacturersTableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading manufacturers">
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Manufacturer</TableHead>
              {LIST_RATING_KINDS.map((kind) => (
                <TableHead key={kind}>{MANUFACTURER_RATINGS[kind].label}</TableHead>
              ))}
              <TableHead>Shipping</TableHead>
              <TableHead className="text-right">Competitor prices</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-24" />
                    <div className="flex gap-1.5">
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                  </div>
                </TableCell>
                {LIST_RATING_KINDS.map((kind) => (
                  <TableCell key={kind}>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                ))}
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-end gap-1">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 lg:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-lg border bg-card p-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function ManufacturersTable({ items }: { items: Manufacturer[] }) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Manufacturer</TableHead>
              {LIST_RATING_KINDS.map((kind) => (
                <TableHead key={kind}>{MANUFACTURER_RATINGS[kind].label}</TableHead>
              ))}
              <TableHead>Shipping</TableHead>
              <TableHead className="text-right">Competitor prices</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((mfr) => {
              const place = location(mfr)
              const shipping = shippingFacts(mfr)
              const competitors = competitorFacts(mfr)
              return (
                <TableRow key={mfr.id} className="align-top">
                  <TableCell className="max-w-[360px] whitespace-normal">
                    <div className="space-y-0.5">
                      <Link
                        to="/manufacturers/$manuId"
                        params={{ manuId: mfr.uuid }}
                        className="font-medium text-primary hover:underline"
                      >
                        {mfr.name}
                      </Link>
                      {place && <p className="text-xs text-muted-foreground">{place}</p>}
                      {mfr.preferred_supplier && (
                        <p className="text-xs text-muted-foreground">
                          Buys through{' '}
                          <Link
                            to="/suppliers/$supplierId"
                            params={{ supplierId: mfr.preferred_supplier.uuid }}
                            className="text-primary hover:underline"
                          >
                            {mfr.preferred_supplier.name}
                          </Link>
                        </p>
                      )}
                      {(mfr.status || mfr.payment_terms_type || trackingLabels(mfr).length > 0) && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                          {mfr.status && <StatusBadge status={mfr.status} className="text-xs" />}
                          <TermsPill mfr={mfr} />
                          <TrackingBadges mfr={mfr} />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  {LIST_RATING_KINDS.map((kind) => (
                    <TableCell key={kind} className="max-w-[240px] whitespace-normal">
                      <RatingChips mfr={mfr} kind={kind} />
                    </TableCell>
                  ))}
                  <TableCell className="max-w-[300px] whitespace-normal">
                    {shipping.length || freightProgram(mfr) ? (
                      <div className="space-y-0.5 text-xs">
                        <FreightProgram mfr={mfr} linkMore />
                        {shipping.map((fact) => (
                          <p key={fact}>{fact}</p>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {competitors.length ? (
                      <div className="space-y-0.5 text-xs tabular-nums">
                        {competitors.map((fact) => (
                          <p key={fact}>{fact}</p>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile and tablet cards */}
      <div className="space-y-2 lg:hidden">
        {items.map((mfr) => {
          const place = location(mfr)
          const facts = [...shippingFacts(mfr), ...competitorFacts(mfr)]
          const rated = LIST_RATING_KINDS.filter((kind) => ratingValues(mfr, kind).length > 0)
          return (
            <Link
              key={mfr.id}
              to="/manufacturers/$manuId"
              params={{ manuId: mfr.uuid }}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium text-primary">{mfr.name}</p>
                  {place && <p className="truncate text-xs text-muted-foreground">{place}</p>}
                  {mfr.preferred_supplier && (
                    <p className="truncate text-xs text-muted-foreground">
                      Buys through {mfr.preferred_supplier.name}
                    </p>
                  )}
                </div>
                {mfr.status && <StatusBadge status={mfr.status} className="shrink-0" />}
              </div>

              {(trackingLabels(mfr).length > 0 || rated.length > 0 || mfr.payment_terms_type) && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <TermsPill mfr={mfr} />
                  <TrackingBadges mfr={mfr} />
                  {rated.map((kind) => (
                    <div key={kind} className="flex flex-wrap items-center gap-1">
                      <span className="text-[11px] text-muted-foreground">
                        {MANUFACTURER_RATINGS[kind].label}
                      </span>
                      <RatingChips mfr={mfr} kind={kind} />
                    </div>
                  ))}
                </div>
              )}

              {(freightProgram(mfr) || facts.length > 0) && (
                <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                  <FreightProgram mfr={mfr} linkMore={false} />
                  {facts.length > 0 && (
                    <p className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {facts.map((fact) => (
                        <span key={fact}>{fact}</span>
                      ))}
                    </p>
                  )}
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </>
  )
}
